// src/components/dashboard/admin/AdminOrganisationDetail.tsx
// React Router route: /admin/organisations/:id
// NEW SUGGESTED NAME: OrganisationDetail.tsx (more descriptive than [id].tsx)

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useAdmin } from '@/contexts/AdminContext.tsx';
import { adminApi } from '@/lib/api/adminApi';
import { OrgLogo } from '@/components/custom-ui/OrgLogo';
import { AdminList, AdminListRow } from './AdminList';
import { EntityActionMenu } from '@/components/custom-ui/EntityActionMenu';
import { EmptyState } from '@/components/custom-ui/empty-state';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { Card, CardContent } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Separator } from '@/components/ui/separator.tsx';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
    Building2,
    Building,
    Users,
    ArrowLeft,
    Plus,
    Mail,
    Phone,
    MapPin,
    Edit,
    User,
    Trash2,
} from 'lucide-react';
import {
    UpdateOrganisationRequest,
    UpdateUnitRequest,
    UpdateEmployerRequest,
} from '@/lib/models/employer';

// Import Nexilink components
import {
    ActionButton,
    OutlineButton,
    DangerButton,
    DeleteConfirmDialog,
    IconChip,
    StatCard,
    StatusBadge,
} from '@/components/custom-ui';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW } from '@/lib/nexilink-card-styles';

// ✅ EMAIL VALIDATION HELPER
const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// One label/value pair in the Organisationsdetaljer block. Renders nothing when
// the value is missing, so the block never shows an empty row.
const Fact: React.FC<{ label: string; value?: string | null; icon?: React.ComponentType<{ className?: string }> }> = ({ label, value, icon: Icon }) => {
    if (!value) return null;
    return (
        <div className="min-w-0">
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="text-sm font-medium text-gray-900 flex items-center gap-1.5 truncate">
                {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                <span className="truncate">{value}</span>
            </dd>
        </div>
    );
};

const OrganisationDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const organisationId = id as string;
    const { toast } = useToast();

    const {
        selectedOrganisation,
        getOrganisationDetails,
        isLoadingOrganisationDetails,
        updateOrganisation,
        deleteOrganisation,
        updateUnit,
        deleteUnit,
        updateEmployer,
        deleteEmployer,
        refreshDashboard,
        createEmployer,
        createUnit,
    } = useAdmin();

    // ==================== DIALOG STATES ====================
    const [editOrgDialogOpen, setEditOrgDialogOpen] = useState(false);
    const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
    const [editUnitDialogOpen, setEditUnitDialogOpen] = useState(false);
    const [deleteUnitDialogOpen, setDeleteUnitDialogOpen] = useState(false);
    const [editEmployerDialogOpen, setEditEmployerDialogOpen] = useState(false);
    const [deleteEmployerDialogOpen, setDeleteEmployerDialogOpen] = useState(false);
    const [addEmployerDialogOpen, setAddEmployerDialogOpen] = useState(false);
    // #358 — "Lägg till Enhet" used to navigate to the create-everything wizard
    // with ?orgId=. That wizard now only ever creates a NEW organisation, so
    // adding a unit to an existing one belongs here, next to the units it joins.
    const [addUnitDialogOpen, setAddUnitDialogOpen] = useState(false);
    const [newUnit, setNewUnit] = useState({ name: '', city: '', description: '' });

    // ==================== EDIT DATA STATES ====================
    const [editingOrg, setEditingOrg] = useState<UpdateOrganisationRequest>({});
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const [editingUnit, setEditingUnit] = useState<{ id: string; data: UpdateUnitRequest }>({ id: '', data: {} });
    const [editingEmployer, setEditingEmployer] = useState<{ id: string; data: UpdateEmployerRequest }>({ id: '', data: {} });
    const [newEmployer, setNewEmployer] = useState<{
        unitId: string;
        unitName: string;
        email: string;
        firstName: string;
        lastName: string;
        jobTitle: string;
    }>({
        unitId: '',
        unitName: '',
        email: '',
        firstName: '',
        lastName: '',
        jobTitle: '',
    });

    // ==================== DELETE TARGET STATES ====================
    const [deleteTargetUnit, setDeleteTargetUnit] = useState<{ id: string; name: string }>({ id: '', name: '' });
    const [deleteTargetEmployer, setDeleteTargetEmployer] = useState<{ id: string; name: string }>({ id: '', name: '' });

    // ==================== LOADING STATES ====================
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // ==================== LOAD ORGANISATION ====================

    useEffect(() => {
        if (organisationId) {
            getOrganisationDetails(organisationId);
        }
    }, [organisationId]);

    // ==================== ORGANISATION HANDLERS ====================
    const handleEditOrg = () => {
        if (!selectedOrganisation) return;
        setEditingOrg({
            name: selectedOrganisation.organisation.name,
            org_number: selectedOrganisation.organisation.org_number || undefined,
            contact_name: selectedOrganisation.organisation.contact_name || undefined,
            contact_email: selectedOrganisation.organisation.contact_email || undefined,
            contact_phone: selectedOrganisation.organisation.contact_phone || undefined,
            address: selectedOrganisation.organisation.address || undefined,
            postal_code: selectedOrganisation.organisation.postal_code || undefined,
            city: selectedOrganisation.organisation.city || undefined,
            is_active: selectedOrganisation.organisation.is_active,
        });
        setEditOrgDialogOpen(true);
    };

    const handleSaveOrg = async () => {
        if (!selectedOrganisation) return;
        setIsUpdating(true);
        try {
            const result = await updateOrganisation(selectedOrganisation.organisation.id, editingOrg);
            if (result) {
                toast({ title: 'Organisation uppdaterad', duration: 3000 });
                setEditOrgDialogOpen(false);
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file || !selectedOrganisation) return;
        if (!file.type.startsWith('image/')) {
            toast({ title: 'Ogiltig fil', description: 'Logotypen måste vara en bild.', variant: 'destructive' });
            return;
        }
        setUploadingLogo(true);
        try {
            await adminApi.uploadOrganisationLogo(selectedOrganisation.organisation.id, file);
            toast({ title: 'Logotyp uppdaterad', duration: 3000 });
            await getOrganisationDetails(selectedOrganisation.organisation.id);
        } catch (error: any) {
            toast({ title: 'Fel', description: error?.message || 'Kunde inte ladda upp logotypen', variant: 'destructive' });
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleDeleteOrg = async () => {
        if (!selectedOrganisation) return;
        setIsDeleting(true);
        try {
            const success = await deleteOrganisation(selectedOrganisation.organisation.id);
            if (success) {
                toast({ title: 'Organisation raderad', duration: 3000 });
                setDeleteOrgDialogOpen(false);
                await refreshDashboard();
                navigate('/dashboard');
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsDeleting(false);
        }
    };

    // ==================== UNIT HANDLERS ====================
    const handleEditUnit = (unitId: string, unitName: string, description?: string, contactName?: string, contactEmail?: string, contactPhone?: string) => {
        setEditingUnit({
            id: unitId,
            data: {
                name: unitName,
                description: description || undefined,
                contact_name: contactName || undefined,
                contact_email: contactEmail || undefined,
                contact_phone: contactPhone || undefined,
            }
        });
        setEditUnitDialogOpen(true);
    };

    const handleSaveUnit = async () => {
        setIsUpdating(true);
        try {
            const result = await updateUnit(editingUnit.id, editingUnit.data);
            if (result) {
                toast({ title: 'Enhet uppdaterad', duration: 3000 });
                setEditUnitDialogOpen(false);
                if (selectedOrganisation) {
                    await getOrganisationDetails(selectedOrganisation.organisation.id);
                }
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteUnit = async () => {
        setIsDeleting(true);
        try {
            const success = await deleteUnit(deleteTargetUnit.id);
            if (success) {
                toast({ title: 'Enhet raderad', duration: 3000 });
                setDeleteUnitDialogOpen(false);
                if (selectedOrganisation) {
                    await getOrganisationDetails(selectedOrganisation.organisation.id);
                }
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsDeleting(false);
        }
    };

    // ==================== EMPLOYER HANDLERS ====================
    const handleEditEmployer = (employerId: string, jobTitle?: string, isActive?: boolean) => {
        setEditingEmployer({
            id: employerId,
            data: {
                job_title: jobTitle || undefined,
                is_active: isActive,
            }
        });
        setEditEmployerDialogOpen(true);
    };

    // ==================== ADD EMPLOYER HANDLER ====================
    const handleAddUnit = async () => {
        if (!newUnit.name.trim() || !organisationId) return;
        setIsUpdating(true);
        try {
            const created = await createUnit({
                organisation_id: organisationId,
                name: newUnit.name.trim(),
                city: newUnit.city.trim() || undefined,
                description: newUnit.description.trim() || undefined,
            });
            if (!created) throw new Error('Kunde inte skapa enheten.');
            toast({ title: 'Enheten är skapad', description: `${newUnit.name.trim()} finns nu i organisationen.` });
            setNewUnit({ name: '', city: '', description: '' });
            setAddUnitDialogOpen(false);
            await getOrganisationDetails(organisationId);
        } catch (err: unknown) {
            toast({
                title: 'Kunde inte skapa enheten',
                description: (err as Error)?.message || 'Försök igen.',
                variant: 'destructive',
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAddEmployer = async () => {
        // ✅ VALIDATE EMAIL FORMAT
        if (!isValidEmail(newEmployer.email)) {
            toast({
                title: 'Ogiltig e-postadress',
                description: 'Ange en giltig e-postadress (t.ex. namn@exempel.se)',
                variant: 'destructive'
            });
            return;
        }

        setIsUpdating(true);
        try {
            // #355 — no password is sent. The backend creates the account without
            // one (it cannot be signed into in that state) and mails the employer
            // a link to choose their own.
            const result = await createEmployer({
                email: newEmployer.email.trim(), // ✅ TRIM WHITESPACE
                first_name: newEmployer.firstName.trim(),
                last_name: newEmployer.lastName.trim(),
                unit_id: newEmployer.unitId,
                job_title: newEmployer.jobTitle?.trim() || undefined,
            });

            if (!result) {
                throw new Error('Kunde inte skapa arbetsgivare');
            }

            toast({
                title: 'Arbetsgivare skapad',
                description: `${newEmployer.firstName} ${newEmployer.lastName} har lagts till`,
                duration: 3000
            });
            setAddEmployerDialogOpen(false);

            // Reset form
            setNewEmployer({
                unitId: '',
                unitName: '',
                email: '',
                        firstName: '',
                lastName: '',
                jobTitle: '',
            });

            // Refresh data
            if (selectedOrganisation) {
                await getOrganisationDetails(selectedOrganisation.organisation.id);
            }
        } catch (error: any) {
            console.error('❌ Error creating employer:', error);

            // ✅ PARSE ERROR MESSAGE
            let errorMessage = 'Ett fel uppstod vid skapande av arbetsgivare';

            if (error.message) {
                if (error.message.includes('duplicate') || error.message.includes('already exists')) {
                    errorMessage = 'En användare med denna e-postadress finns redan';
                } else if (error.message.includes('unit not found')) {
                    errorMessage = 'Enheten hittades inte';
                } else if (error.message.includes('unit already has an active employer')) {
                    errorMessage = 'Denna enhet har redan en aktiv arbetsgivare';
                } else {
                    errorMessage = error.message;
                }
            }

            toast({
                title: 'Fel',
                description: errorMessage,
                variant: 'destructive'
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSaveEmployer = async () => {
        setIsUpdating(true);
        try {
            const result = await updateEmployer(editingEmployer.id, editingEmployer.data);
            if (result) {
                toast({ title: 'Arbetsgivare uppdaterad', duration: 3000 });
                setEditEmployerDialogOpen(false);
                if (selectedOrganisation) {
                    await getOrganisationDetails(selectedOrganisation.organisation.id);
                }
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteEmployer = async () => {
        setIsDeleting(true);
        try {
            const success = await deleteEmployer(deleteTargetEmployer.id);
            if (success) {
                toast({ title: 'Arbetsgivare raderad', duration: 3000 });
                setDeleteEmployerDialogOpen(false);
                if (selectedOrganisation) {
                    await getOrganisationDetails(selectedOrganisation.organisation.id);
                }
            }
        } catch (error: any) {
            toast({ title: 'Fel', description: error.message, variant: 'destructive' });
        } finally {
            setIsDeleting(false);
        }
    };

    // ==================== LOADING STATE ====================

    if (isLoadingOrganisationDetails) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-64 mb-6" />
                <div className="space-y-6">
                    <Skeleton className="h-48 w-full" />
                    <Skeleton className="h-48 w-full" />
                </div>
            </div>
        );
    }

    if (!selectedOrganisation) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">Organisation hittades inte</p>
                        <OutlineButton
                            className="mt-4"
                            onClick={() => navigate('/dashboard/organisations')}
                        >
                            Tillbaka till organisationer
                        </OutlineButton>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { organisation, units, stats } = selectedOrganisation;

    // ==================== RENDER ====================

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="space-y-4">
                {/* #358 — went to /dashboard, which is not where you came from. */}
                <Button
                    variant="ghost"
                    onClick={() => navigate('/dashboard/organisations')}
                    className="mb-4"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Tillbaka till organisationer
                </Button>

                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                        <OrgLogo name={organisation.name} logoUrl={organisation.logo_url} size="xl" />
                        <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-bold mb-2">
                                {organisation.name}
                            </h1>
                            {!organisation.is_active && (
                                <StatusBadge status="inactive" label="Inaktiv" />
                            )}
                        </div>
                        {organisation.city && (
                            <p className="text-muted-foreground">{organisation.city}</p>
                        )}
                        </div>
                    </div>
                    {/* #358 — "Radera" used to be a solid red button the same size as
                        the primary action, so the most destructive thing on the page
                        was also one of the most prominent. It lives in the kebab now,
                        with the confirm-by-typing dialog behind it unchanged. */}
                    <div className="flex items-center gap-2">
                        <OutlineButton onClick={handleEditOrg} icon={Edit}>
                            Redigera
                        </OutlineButton>
                        <ActionButton onClick={() => setAddUnitDialogOpen(true)} icon={Plus}>
                            Lägg till enhet
                        </ActionButton>
                        <EntityActionMenu
                            actions={[
                                {
                                    label: 'Radera organisation',
                                    icon: Trash2,
                                    destructive: true,
                                    description: 'Raderar även enheter och arbetsgivare.',
                                    onClick: () => setDeleteOrgDialogOpen(true),
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* #358 — the four stat tiles that stood here are gone. They restated
                what the lists below already say, and did it twice over: "Totalt
                Enheter 1 / 0 aktiva" sat beside a separate "Aktiva Enheter 0"
                card. Counts now live in the section headers, where they are a
                label rather than a dashboard. */}

            {/* Organisationsdetaljer */}
            <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <IconChip icon={Building2} />
                        <h2 className="text-sm font-semibold text-gray-900">Organisationsdetaljer</h2>
                    </div>
                    <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                        <Fact label="Organisationsnummer" value={organisation.org_number} />
                        <Fact label="Kontaktperson" value={organisation.contact_name} />
                        <Fact label="E-post" value={organisation.contact_email} icon={Mail} />
                        <Fact label="Telefon" value={organisation.contact_phone} icon={Phone} />
                        <Fact
                            label="Adress"
                            value={[organisation.address, [organisation.postal_code, organisation.city].filter(Boolean).join(' ')]
                                .filter(Boolean).join(', ')}
                            icon={MapPin}
                        />
                    </dl>
                </div>
            </div>

            {/* Enheter — the same AdminList every other admin list uses (#358).
                One row per unit; the unit's employer rides in the trailing slot
                because the MVP allows one per unit, so it reads as an attribute
                of the unit rather than a second nested list. Everything that used
                to be a scattered icon-button is in the row's kebab. */}
            <AdminList
                title="Enheter"
                count={units.length}
                action={
                    <ActionButton onClick={() => setAddUnitDialogOpen(true)} icon={Plus}>
                        Lägg till enhet
                    </ActionButton>
                }
                empty={
                    <EmptyState
                        icon={Building}
                        title="Inga enheter ännu"
                        description="En enhet är en avdelning inuti organisationen. Arbetsgivare kopplas till en enhet."
                        action={{ label: 'Lägg till enhet', onClick: () => setAddUnitDialogOpen(true), icon: Plus }}
                    />
                }
            >
                {units.map((unit) => {
                    const employers = unit.employers ?? [];
                    const primary = employers[0];
                    const contactLine = [unit.contact_name, unit.contact_email, unit.city]
                        .filter(Boolean).join(' · ');
                    return (
                        <AdminListRow
                            key={unit.id}
                            leading={<IconChip icon={Building} />}
                            title={
                                <span className="flex items-center gap-2">
                                    <span className="truncate">{unit.name}</span>
                                    {!unit.is_active && <StatusBadge status="inactive" label="Inaktiv" />}
                                </span>
                            }
                            subtitle={unit.description || contactLine || '—'}
                            trailing={
                                <>
                                    {primary ? (
                                        <button
                                            type="button"
                                            onClick={() => handleEditEmployer(
                                                primary.employer.id,
                                                primary.employer.job_title || undefined,
                                                primary.employer.is_active,
                                            )}
                                            className="hidden md:flex items-center gap-2 max-w-[240px] group"
                                            title={`${primary.user.full_name} · ${primary.user.email}`}
                                        >
                                            <Avatar className="w-8 h-8">
                                                <AvatarImage src={resolveMediaUrl(primary.user.picture_url)} />
                                                <AvatarFallback className="bg-[#A4C3B2]/40 text-[#1A4D4F] text-xs">
                                                    {(primary.user.full_name || primary.user.email || '?').slice(0, 1).toUpperCase()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="min-w-0 text-left">
                                                <span className="block text-sm text-gray-900 truncate group-hover:underline">
                                                    {primary.user.full_name || primary.user.email}
                                                </span>
                                                <span className="block text-xs text-gray-400 truncate">
                                                    {employers.length > 1
                                                        ? `+${employers.length - 1} till`
                                                        : primary.employer.job_title || 'Arbetsgivare'}
                                                </span>
                                            </span>
                                            {!primary.employer.is_active && <StatusBadge status="inactive" label="Inaktiv" />}
                                        </button>
                                    ) : (
                                        <OutlineButton
                                            size="sm"
                                            icon={Plus}
                                            onClick={() => {
                                                setNewEmployer({
                                                    unitId: unit.id, unitName: unit.name,
                                                    email: '', firstName: '', lastName: '', jobTitle: '',
                                                });
                                                setAddEmployerDialogOpen(true);
                                            }}
                                        >
                                            Arbetsgivare
                                        </OutlineButton>
                                    )}
                                    <EntityActionMenu
                                        actions={[
                                            {
                                                label: 'Redigera enhet',
                                                icon: Edit,
                                                onClick: () => handleEditUnit(
                                                    unit.id, unit.name, unit.description,
                                                    unit.contact_name, unit.contact_email, unit.contact_phone,
                                                ),
                                            },
                                            primary && {
                                                label: 'Redigera arbetsgivare',
                                                icon: User,
                                                onClick: () => handleEditEmployer(
                                                    primary.employer.id,
                                                    primary.employer.job_title || undefined,
                                                    primary.employer.is_active,
                                                ),
                                            },
                                            !primary && {
                                                label: 'Lägg till arbetsgivare',
                                                icon: Plus,
                                                onClick: () => {
                                                    setNewEmployer({
                                                        unitId: unit.id, unitName: unit.name,
                                                        email: '', firstName: '', lastName: '', jobTitle: '',
                                                    });
                                                    setAddEmployerDialogOpen(true);
                                                },
                                            },
                                            primary && {
                                                label: 'Radera arbetsgivare',
                                                icon: Trash2,
                                                destructive: true,
                                                onClick: () => {
                                                    setDeleteTargetEmployer({ id: primary.employer.id, name: primary.user.full_name });
                                                    setDeleteEmployerDialogOpen(true);
                                                },
                                            },
                                            {
                                                label: 'Radera enhet',
                                                icon: Trash2,
                                                destructive: true,
                                                onClick: () => {
                                                    setDeleteTargetUnit({ id: unit.id, name: unit.name });
                                                    setDeleteUnitDialogOpen(true);
                                                },
                                            },
                                        ]}
                                    />
                                </>
                            }
                        />
                    );
                })}
            </AdminList>

            {/* ==================== DIALOGS ==================== */}

            {/* Edit Organisation Dialog */}
            <Dialog open={editOrgDialogOpen} onOpenChange={setEditOrgDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Redigera Organisation</DialogTitle>
                        <DialogDescription>Uppdatera organisationsinformation</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        {/* Logo (#40) — upload or replace; fallback is a name-derived colored placeholder */}
                        <div className="flex items-center gap-4">
                            <OrgLogo
                                name={selectedOrganisation?.organisation.name}
                                logoUrl={selectedOrganisation?.organisation.logo_url}
                                size="xl"
                            />
                            <div className="space-y-1">
                                <Label>Logotyp</Label>
                                <div>
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleLogoChange}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={uploadingLogo}
                                        onClick={() => logoInputRef.current?.click()}
                                    >
                                        {uploadingLogo ? 'Laddar upp…' : selectedOrganisation?.organisation.logo_url ? 'Byt logotyp' : 'Ladda upp logotyp'}
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">PNG eller JPG. Utan logotyp visas en färgad platshållare.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="org-name">Namn *</Label>
                                <Input
                                    id="org-name"
                                    value={editingOrg.name || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="org-number">Organisationsnummer</Label>
                                <Input
                                    id="org-number"
                                    value={editingOrg.org_number || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, org_number: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="org-contact-name">Kontaktperson</Label>
                                <Input
                                    id="org-contact-name"
                                    value={editingOrg.contact_name || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, contact_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="org-contact-email">Kontakt E-post</Label>
                                <Input
                                    id="org-contact-email"
                                    type="email"
                                    value={editingOrg.contact_email || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, contact_email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="org-contact-phone">Telefon</Label>
                            <Input
                                id="org-contact-phone"
                                value={editingOrg.contact_phone || ''}
                                onChange={(e) => setEditingOrg({ ...editingOrg, contact_phone: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="org-address">Adress</Label>
                            <Input
                                id="org-address"
                                value={editingOrg.address || ''}
                                onChange={(e) => setEditingOrg({ ...editingOrg, address: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="org-postal">Postnummer</Label>
                                <Input
                                    id="org-postal"
                                    value={editingOrg.postal_code || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, postal_code: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="org-city">Stad</Label>
                                <Input
                                    id="org-city"
                                    value={editingOrg.city || ''}
                                    onChange={(e) => setEditingOrg({ ...editingOrg, city: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="org-active"
                                checked={editingOrg.is_active || false}
                                onChange={(e) => setEditingOrg({ ...editingOrg, is_active: e.target.checked })}
                                className="h-4 w-4"
                            />
                            <Label htmlFor="org-active">Aktiv</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <OutlineButton onClick={() => setEditOrgDialogOpen(false)}>
                            Avbryt
                        </OutlineButton>
                        <ActionButton onClick={handleSaveOrg} loading={isUpdating}>
                            {isUpdating ? 'Sparar...' : 'Spara'}
                        </ActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Unit Dialog (#358) — name only; the rest is editable afterwards
                via "Redigera Enhet", so this asks for the one thing it must. */}
            <Dialog open={addUnitDialogOpen} onOpenChange={setAddUnitDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Lägg till Enhet</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-unit-name">Namn <span className="text-red-500">*</span></Label>
                            <Input
                                id="new-unit-name"
                                value={newUnit.name}
                                onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                                placeholder="t.ex. Barn & Unga"
                                autoFocus
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-unit-city">Stad</Label>
                                <Input
                                    id="new-unit-city"
                                    value={newUnit.city}
                                    onChange={(e) => setNewUnit({ ...newUnit, city: e.target.value })}
                                    placeholder="t.ex. Malmö"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new-unit-description">Beskrivning</Label>
                            <Textarea
                                id="new-unit-description"
                                rows={3}
                                value={newUnit.description}
                                onChange={(e) => setNewUnit({ ...newUnit, description: e.target.value })}
                                placeholder="Kort beskrivning av enheten…"
                            />
                        </div>
                        <p className="text-sm text-gray-500">
                            Arbetsgivare lägger du till på enheten när den finns.
                        </p>
                    </div>
                    <DialogFooter>
                        <OutlineButton onClick={() => setAddUnitDialogOpen(false)}>Avbryt</OutlineButton>
                        <ActionButton
                            onClick={handleAddUnit}
                            loading={isUpdating}
                            disabled={!newUnit.name.trim()}
                        >
                            {isUpdating ? 'Skapar…' : 'Skapa enhet'}
                        </ActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Organisation Dialog */}
            <DeleteConfirmDialog
                open={deleteOrgDialogOpen}
                onOpenChange={setDeleteOrgDialogOpen}
                title="Radera Organisation"
                entityLabel="organisationen"
                entityName={organisation.name}
                cascadeWarning="Detta raderar även alla enheter och arbetsgivare som tillhör organisationen."
                onConfirm={handleDeleteOrg}
                isDeleting={isDeleting}
            />

            {/* Edit Unit Dialog */}
            <Dialog open={editUnitDialogOpen} onOpenChange={setEditUnitDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Redigera Enhet</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="unit-name">Namn *</Label>
                            <Input
                                id="unit-name"
                                value={editingUnit.data.name || ''}
                                onChange={(e) => setEditingUnit({
                                    ...editingUnit,
                                    data: { ...editingUnit.data, name: e.target.value }
                                })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="unit-description">Beskrivning</Label>
                            <Textarea
                                id="unit-description"
                                value={editingUnit.data.description || ''}
                                onChange={(e) => setEditingUnit({
                                    ...editingUnit,
                                    data: { ...editingUnit.data, description: e.target.value }
                                })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="unit-contact-name">Kontaktperson</Label>
                            <Input
                                id="unit-contact-name"
                                value={editingUnit.data.contact_name || ''}
                                onChange={(e) => setEditingUnit({
                                    ...editingUnit,
                                    data: { ...editingUnit.data, contact_name: e.target.value }
                                })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="unit-contact-email">E-post</Label>
                            <Input
                                id="unit-contact-email"
                                type="email"
                                value={editingUnit.data.contact_email || ''}
                                onChange={(e) => setEditingUnit({
                                    ...editingUnit,
                                    data: { ...editingUnit.data, contact_email: e.target.value }
                                })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="unit-contact-phone">Telefon</Label>
                            <Input
                                id="unit-contact-phone"
                                value={editingUnit.data.contact_phone || ''}
                                onChange={(e) => setEditingUnit({
                                    ...editingUnit,
                                    data: { ...editingUnit.data, contact_phone: e.target.value }
                                })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <OutlineButton onClick={() => setEditUnitDialogOpen(false)}>
                            Avbryt
                        </OutlineButton>
                        <ActionButton onClick={handleSaveUnit} loading={isUpdating}>
                            {isUpdating ? 'Sparar...' : 'Spara'}
                        </ActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Unit Dialog */}
            <DeleteConfirmDialog
                open={deleteUnitDialogOpen}
                onOpenChange={setDeleteUnitDialogOpen}
                title="Radera Enhet"
                entityLabel="enheten"
                entityName={deleteTargetUnit.name}
                cascadeWarning="Detta raderar även alla arbetsgivare som tillhör enheten."
                onConfirm={handleDeleteUnit}
                isDeleting={isDeleting}
            />

            {/* Edit Employer Dialog */}
            <Dialog open={editEmployerDialogOpen} onOpenChange={setEditEmployerDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Redigera Arbetsgivare</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="emp-job-title">Jobbtitel</Label>
                            <Input
                                id="emp-job-title"
                                value={editingEmployer.data.job_title || ''}
                                onChange={(e) => setEditingEmployer({
                                    ...editingEmployer,
                                    data: { ...editingEmployer.data, job_title: e.target.value }
                                })}
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="emp-active"
                                checked={editingEmployer.data.is_active || false}
                                onChange={(e) => setEditingEmployer({
                                    ...editingEmployer,
                                    data: { ...editingEmployer.data, is_active: e.target.checked }
                                })}
                                className="h-4 w-4"
                            />
                            <Label htmlFor="emp-active">Aktiv</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <OutlineButton onClick={() => setEditEmployerDialogOpen(false)}>
                            Avbryt
                        </OutlineButton>
                        <ActionButton onClick={handleSaveEmployer} loading={isUpdating}>
                            {isUpdating ? 'Sparar...' : 'Spara'}
                        </ActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Employer Dialog */}
            <DeleteConfirmDialog
                open={deleteEmployerDialogOpen}
                onOpenChange={setDeleteEmployerDialogOpen}
                title="Radera Arbetsgivare"
                entityLabel="arbetsgivaren"
                entityName={deleteTargetEmployer.name}
                onConfirm={handleDeleteEmployer}
                isDeleting={isDeleting}
            />

            {/* Add Employer Dialog */}
            <Dialog open={addEmployerDialogOpen} onOpenChange={setAddEmployerDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Lägg till Arbetsgivare</DialogTitle>
                        <DialogDescription>
                            Skapa en ny arbetsgivare för enheten: <strong>{newEmployer.unitName}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-emp-email">E-post *</Label>
                            <Input
                                id="new-emp-email"
                                type="email"
                                placeholder="namn@exempel.se"
                                value={newEmployer.email}
                                onChange={(e) => setNewEmployer({ ...newEmployer, email: e.target.value })}
                                className={
                                    newEmployer.email && !isValidEmail(newEmployer.email)
                                        ? 'border-red-500 focus:border-red-500'
                                        : ''
                                }
                            />
                            {newEmployer.email && !isValidEmail(newEmployer.email) && (
                                <p className="text-sm text-red-500">Ange en giltig e-postadress</p>
                            )}
                        </div>
                        {/* #355 — no password field. The admin used to type one here and
                            then had to tell the employer what it was; nothing was ever
                            sent. Now the account is created without a password and the
                            employer picks their own from an invitation mail. */}
                        <div className="rounded-lg bg-[#F3F7F5] border border-[#A4C3B2]/40 p-3 flex gap-2.5">
                            <Mail className="w-4 h-4 text-[#1A4D4F] flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700">
                                Arbetsgivaren får ett mejl med en länk för att välja sitt eget
                                lösenord. Länken gäller i 7 dagar.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-emp-firstname">Förnamn *</Label>
                                <Input
                                    id="new-emp-firstname"
                                    value={newEmployer.firstName}
                                    onChange={(e) => setNewEmployer({ ...newEmployer, firstName: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="new-emp-lastname">Efternamn *</Label>
                                <Input
                                    id="new-emp-lastname"
                                    value={newEmployer.lastName}
                                    onChange={(e) => setNewEmployer({ ...newEmployer, lastName: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new-emp-jobtitle">Jobbtitel</Label>
                            <Input
                                id="new-emp-jobtitle"
                                placeholder="T.ex. Enhetschef, HR-ansvarig"
                                value={newEmployer.jobTitle}
                                onChange={(e) => setNewEmployer({ ...newEmployer, jobTitle: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <OutlineButton onClick={() => setAddEmployerDialogOpen(false)}>
                            Avbryt
                        </OutlineButton>
                        <ActionButton
                            onClick={handleAddEmployer}
                            loading={isUpdating}
                            disabled={
                                !newEmployer.email ||
                                !isValidEmail(newEmployer.email) ||  // ✅ EMAIL FORMAT CHECK
                                !newEmployer.firstName ||
                                !newEmployer.lastName
                            }
                        >
                            {isUpdating ? 'Skapar...' : 'Skapa Arbetsgivare'}
                        </ActionButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrganisationDetails;